import { Button } from "design-system/atoms/Button"
import {
  Dialog as DesignDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { Ellipsis } from "lucide-react"
import { useArchiveEmployeeMutation } from "modules/shared/hooks/useArchiveEmployeeMutation"
import { useSetOwnerMutation } from "modules/shared/hooks/useSetOwnerMutation"
import { useImpersonateEmployeeMutation } from "modules/settings/hooks/useImpersonateEmployeeMutation"
import { useUnarchiveEmployeeMutation } from "modules/shared/hooks/useUnarchiveEmployeeMutation"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { EmployeeResponseData } from "schema"
import { toast } from "sonner"

interface DialogProps {
  employee: EmployeeResponseData
  isOwner: boolean
  isSuperadmin: boolean
}

export function ArchiveEmployee({ employee, isOwner, isSuperadmin }: DialogProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isSetOwnerOpen, setIsSetOwnerOpen] = useState(false)

  const { mutateAsync: onArchiveEmployee, isPending: isArchivePending } =
    useArchiveEmployeeMutation()
  const { mutateAsync: onUnarchiveEmployee, isPending: isUnarchivePending } =
    useUnarchiveEmployeeMutation()
  const { mutateAsync: onSetOwner, isPending: isSetOwnerPending } = useSetOwnerMutation()
  const { mutateAsync: onImpersonateEmployee, isPending: isImpersonatePending } =
    useImpersonateEmployeeMutation()

  const shouldShowOwnerActions = isOwner && !employee.isOwner
  const shouldShowImpersonate = isSuperadmin && !employee.archivedAt

  if (!shouldShowOwnerActions && !shouldShowImpersonate) {
    return null
  }

  const isPending =
    isArchivePending || isUnarchivePending || isSetOwnerPending || isImpersonatePending
  const isArchive = !employee.archivedAt

  async function onArchive() {
    await onArchiveEmployee({ id: ensure(employee).id })

    toast.success(t("settings.team.table.archiveDialog.archive.success"))
    setIsOpen(false)
  }

  async function onUnarchive() {
    await onUnarchiveEmployee({ id: ensure(employee).id })

    toast.success(t("settings.team.table.archiveDialog.unarchive.success"))
    setIsOpen(false)
  }

  async function handleSetOwner() {
    await onSetOwner({ id: ensure(employee).id })

    toast.success(t("settings.team.table.setOwnerSuccess"))
    setIsSetOwnerOpen(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" disabled={isPending}>
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {shouldShowImpersonate && (
            <DropdownMenuItem onClick={() => onImpersonateEmployee(employee.id)}>
              {t("settings.team.table.actions.impersonate")}
            </DropdownMenuItem>
          )}
          {shouldShowOwnerActions && !employee.archivedAt && (
            <DropdownMenuItem onClick={() => setIsSetOwnerOpen(true)}>
              {t("settings.team.table.actions.setOwner")}
            </DropdownMenuItem>
          )}
          {shouldShowOwnerActions && (
            <DropdownMenuItem
              className={cn("flex items-center gap-2", !employee.archivedAt && "text-destructive")}
              onClick={() => setIsOpen(true)}
            >
              {employee.archivedAt
                ? t("settings.team.table.actions.unarchive")
                : t("settings.team.table.actions.archive")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DesignDialog open={isSetOwnerOpen} onOpenChange={setIsSetOwnerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.team.table.setOwnerDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("settings.team.table.setOwnerDialog.description", {
                name: employee.name ?? employee.email,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsSetOwnerOpen(false)}>
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleSetOwner} disabled={isSetOwnerPending}>
              {t("settings.team.table.setOwnerDialog.cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DesignDialog>
      <DesignDialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isArchive
                ? t("settings.team.table.archiveDialog.archive.title")
                : t("settings.team.table.archiveDialog.unarchive.title")}
            </DialogTitle>
            <DialogDescription>
              {isArchive
                ? t("settings.team.table.archiveDialog.archive.description")
                : t("settings.team.table.archiveDialog.unarchive.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              {t("shared.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={isArchive ? onArchive : onUnarchive}
              disabled={isPending}
            >
              {isArchive
                ? t("settings.team.table.archiveDialog.archive.cta")
                : t("settings.team.table.archiveDialog.unarchive.cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DesignDialog>
    </>
  )
}
